import { useEffect, useMemo, useRef, useState } from "react";
import {
  Aviso,
  Botao,
  Cabecalho,
  Campo,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Tabela,
} from "../componentes/ui";
import { levarAoFormulario } from "../nucleo/foco";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";
import {
  useAlterarRecursoPlus,
  useCatalogo,
  useCriarRecursoPlus,
  useRecursosPlus,
  type RecursoDoCatalogo,
  type RecursoParaGravar,
} from "../nucleo/implantacoes";
import { useVoltarParaHome } from "../nucleo/voltarParaHome";

/**
 * O cadastro do catálogo de Recursos Plus — a tela de quem administra o portal.
 *
 * Existe porque o catálogo era **dado semeado em migration**: incluir um recurso que a área
 * comercial passou a vender exigia uma migração e um deploy. Enquanto isso, o que foi vendido
 * some entre o contrato e a execução, que é justamente o que o catálogo existe para impedir.
 *
 * Duas decisões que a tela precisa deixar visíveis, porque são elas que a tornam segura:
 *
 * * **remover é desativar.** A chave é a identidade que `implantacao_recurso_plus` guarda; apagar
 *   romperia a chave estrangeira de quem já contratou o recurso e apagaria o registro do que foi
 *   vendido. Desativado, ele some do cadastro de implantação e continua aqui, reativável;
 * * **recurso sem módulo é legítimo.** "Estoque por Data" serve ao Força de Vendas e ao B2B, e o
 *   GuiaRep vale para a operação inteira. O campo aceita vazio, e vazio significa "não pende de um
 *   módulo único" — não "faltou preencher".
 *
 * O formulário fica **acima** da tabela porque é ele que se usa o tempo todo — mas o catálogo tem
 * vinte e sete linhas, e "Editar" na última preenchia um formulário fora da tela. Por isso o clique
 * leva a pessoa até lá, com o cursor já no primeiro campo: sem isso a tela parece não responder ao
 * clique, que foi exatamente o relato de quem a usa.
 */

const PERMISSAO_DO_CATALOGO = "catalogo:gerenciar";

const VAZIO: RecursoParaGravar & { chave: string } = {
  chave: "",
  nome: "",
  modulo_chave: null,
  jira_project_id: "",
  ordem: 1,
  descricao: null,
  ativo: true,
};

type Erros = Partial<Record<"chave" | "nome" | "jira_project_id" | "ordem", string>>;

export function CatalogoDeRecursos() {
  useTitulo("Catálogo de Recursos Plus");
  /* O menu e a porta do console já não oferecem esta tela a quem não administra o catálogo, mas o
     endereço continua digitável. Sem esta checagem a pessoa recebia o formulário inteiro com
     "não foi possível carregar agora" — a resposta do servidor era um 403 que nunca passa. */
  const sessao = useSessao();
  const podeGerenciar = podeNaSessao(sessao.data, PERMISSAO_DO_CATALOGO);
  const recursos = useRecursosPlus(podeGerenciar);
  const catalogo = useCatalogo(podeGerenciar);
  const criar = useCriarRecursoPlus();
  const voltarParaHome = useVoltarParaHome();
  const alterar = useAlterarRecursoPlus();

  const [emEdicao, setEmEdicao] = useState<string | null>(null);
  const [formulario, setFormulario] = useState(VAZIO);
  const [erros, setErros] = useState<Erros>({});
  const [falha, setFalha] = useState<string | null>(null);

  const secaoDoFormulario = useRef<HTMLElement>(null);
  /* Um contador, e não `emEdicao`, porque clicar "Editar" na MESMA linha uma segunda vez não muda
     estado nenhum — e é justamente o clique de quem rolou a tabela de volta e quer voltar ao
     formulário. Com `emEdicao` na dependência, esse clique não faria nada. */
  const [pedidosDeEdicao, setPedidosDeEdicao] = useState(0);

  useEffect(() => {
    if (pedidosDeEdicao === 0) return;
    levarAoFormulario(secaoDoFormulario.current);
  }, [pedidosDeEdicao]);

  const nomeDoModulo = useMemo(() => {
    const mapa = new Map((catalogo.data?.modulos ?? []).map((m) => [m.chave, m.nome]));
    return (chave: string | null) => (chave === null ? "—" : (mapa.get(chave) ?? chave));
  }, [catalogo.data]);

  const nomeDoProjetoJira = useMemo(() => {
    const mapa = new Map(
      (catalogo.data?.projetos_jira_dos_recursos_plus ?? []).map((p) => [p.valor, p.rotulo]),
    );
    return (id: string | null) => (id === null ? "A definir" : (mapa.get(id) ?? id));
  }, [catalogo.data]);

  const criando = emEdicao === null;

  function abrirNovo() {
    setEmEdicao(null);
    setFormulario({ ...VAZIO, ordem: (recursos.data?.length ?? 0) + 1 });
    setErros({});
    setFalha(null);
  }

  function abrirEdicao(recurso: RecursoDoCatalogo) {
    setEmEdicao(recurso.chave);
    setFormulario({
      chave: recurso.chave,
      nome: recurso.nome,
      modulo_chave: recurso.modulo_chave,
      jira_project_id: recurso.jira_project_id ?? "",
      ordem: recurso.ordem,
      descricao: recurso.descricao,
      ativo: recurso.ativo,
    });
    setErros({});
    setFalha(null);
    setPedidosDeEdicao((quantos) => quantos + 1);
  }

  function validar(): Erros {
    const encontrados: Erros = {};
    if (formulario.nome.trim().length < 2) encontrados.nome = "Informe o nome do recurso.";
    if (!formulario.jira_project_id) {
      encontrados.jira_project_id = "Escolha o módulo usado pelo Jira.";
    }
    if (!Number.isFinite(formulario.ordem) || formulario.ordem < 1) {
      encontrados.ordem = "A ordem começa em 1.";
    }
    if (criando && !/^[a-z][a-z0-9_]*$/.test(formulario.chave)) {
      encontrados.chave = "Use letras minúsculas, números e _ — começando por letra.";
    }
    return encontrados;
  }

  async function gravar() {
    const encontrados = validar();
    setErros(encontrados);
    setFalha(null);
    if (Object.keys(encontrados).length > 0) return;
    const corpo = {
      ...formulario,
      nome: formulario.nome.trim(),
      descricao: formulario.descricao?.trim() ? formulario.descricao.trim() : null,
    };
    try {
      if (criando) await criar.mutateAsync(corpo);
      else await alterar.mutateAsync({ ...corpo, chave: emEdicao });
      setFormulario(VAZIO);
      setEmEdicao(null);
      voltarParaHome(criando ? "Recurso Plus criado." : "Recurso Plus salvo.");
    } catch (erro) {
      /* A API responde com a mensagem escrita para quem está do outro lado da tela — "Já existe um
         Recurso Plus com a chave 'cubo'" diz o que fazer; "a API respondeu 409" não. */
      setFalha(erro instanceof Error ? erro.message : "Não foi possível gravar.");
    }
  }

  if (!sessao.data) {
    return (
      <p className="lead" role="status">
        Verificando o seu acesso…
      </p>
    );
  }

  if (!podeGerenciar) {
    return (
      <Aviso tom="marca" data-testid="catalogo-sem-permissao">
        O catálogo de Recursos Plus é mantido pela administração do portal. Para incluir ou
        desativar um recurso, peça a quem administra o console.
      </Aviso>
    );
  }

  return (
    <>
      <h1 className="titulo-da-pagina">Catálogo de Recursos Plus</h1>
      <p className="lead">
        O que a área comercial pode vender junto dos módulos. Incluir aqui vale na hora para o
        cadastro de implantação — não precisa de publicação.
      </p>

      {recursos.isError && (
        <Aviso tom="erro" data-testid="falha-do-catalogo">
          Não foi possível carregar o catálogo agora.
        </Aviso>
      )}

      <section className="mb-5 scroll-mt-6" ref={secaoDoFormulario}>
        <h2>{criando ? "Novo Recurso Plus" : `Editando: ${emEdicao}`}</h2>
        {falha && (
          <Aviso tom="erro" data-testid="falha-ao-gravar">
            {falha}
          </Aviso>
        )}
        {criando && (
          <Campo
            rotulo="Chave"
            ajuda="Identificador permanente. Não muda depois de criado."
            obrigatorio
            data-testid="campo-chave"
            value={formulario.chave}
            erro={erros.chave}
            onChange={(evento) =>
              setFormulario((atual) => ({ ...atual, chave: evento.target.value }))
            }
          />
        )}
        <Campo
          rotulo="Nome"
          obrigatorio
          data-testid="campo-nome"
          value={formulario.nome}
          erro={erros.nome}
          onChange={(evento) => setFormulario((atual) => ({ ...atual, nome: evento.target.value }))}
        />
        <label className="campo">
          <span className="campo__rotulo">Módulo</span>
          <select
            className="campo__controle"
            data-testid="campo-modulo"
            value={formulario.modulo_chave ?? ""}
            onChange={(evento) =>
              setFormulario((atual) => ({
                ...atual,
                modulo_chave: evento.target.value === "" ? null : evento.target.value,
              }))
            }
          >
            <option value="">Nenhum — vale para mais de um módulo</option>
            {(catalogo.data?.modulos ?? []).map((modulo) => (
              <option key={modulo.chave} value={modulo.chave}>
                {modulo.nome}
              </option>
            ))}
          </select>
          <span className="campo__ajuda">
            Deixe vazio quando o recurso não pertence a um módulo só. Ele passa a poder ser vendido
            com qualquer contrato.
          </span>
        </label>
        <label className="campo">
          <span className="campo__rotulo">Módulo no Jira *</span>
          <select
            className="campo__controle"
            data-testid="campo-projeto-jira"
            value={formulario.jira_project_id}
            aria-invalid={Boolean(erros.jira_project_id)}
            onChange={(evento) =>
              setFormulario((atual) => ({ ...atual, jira_project_id: evento.target.value }))
            }
          >
            <option value="">Selecione</option>
            {(catalogo.data?.projetos_jira_dos_recursos_plus ?? []).map((projeto) => (
              <option key={projeto.valor} value={projeto.valor}>
                {projeto.rotulo}
              </option>
            ))}
          </select>
          <span className="campo__ajuda">
            Classificação que será enviada ao Jira e define em qual área do portal o recurso aparece.
          </span>
          {erros.jira_project_id && <span className="campo__erro">{erros.jira_project_id}</span>}
        </label>
        <Campo
          rotulo="Ordem"
          type="number"
          min={1}
          data-testid="campo-ordem"
          value={String(formulario.ordem)}
          erro={erros.ordem}
          onChange={(evento) =>
            setFormulario((atual) => ({ ...atual, ordem: Number(evento.target.value) }))
          }
        />
        <Campo
          rotulo="Descrição"
          data-testid="campo-descricao"
          value={formulario.descricao ?? ""}
          onChange={(evento) =>
            setFormulario((atual) => ({ ...atual, descricao: evento.target.value }))
          }
        />
        <label className="campo campo--linha">
          <input
            type="checkbox"
            data-testid="campo-ativo"
            checked={formulario.ativo}
            onChange={(evento) =>
              setFormulario((atual) => ({ ...atual, ativo: evento.target.checked }))
            }
          />
          <span>Disponível para venda</span>
        </label>
        <div className="acoes">
          <Botao
            type="button"
            data-testid="gravar-recurso"
            onClick={() => void gravar()}
            disabled={criar.isPending || alterar.isPending}
          >
            {criando ? "Cadastrar" : "Salvar"}
          </Botao>
          {!criando && (
            <Botao variante="fantasma" type="button" data-testid="cancelar-edicao" onClick={abrirNovo}>
              Cancelar
            </Botao>
          )}
        </div>
      </section>

      <Tabela
        legenda="Recursos Plus do catálogo, com o módulo a que pertencem e se estão à venda."
        data-testid="tabela-de-recursos"
      >
        <Cabecalho>
          <Linha>
            <CelulaCabecalho>Recurso</CelulaCabecalho>
            <CelulaCabecalho>Módulo</CelulaCabecalho>
            <CelulaCabecalho>Módulo no Jira</CelulaCabecalho>
            <CelulaCabecalho>Situação</CelulaCabecalho>
            <CelulaCabecalho>Ação</CelulaCabecalho>
          </Linha>
        </Cabecalho>
        <Corpo>
          {(recursos.data ?? []).map((recurso) => (
            <Linha key={recurso.chave} data-chave={recurso.chave}>
              <Celula>
                <strong>{recurso.nome}</strong>
                {recurso.descricao && <p className="cartao__texto">{recurso.descricao}</p>}
              </Celula>
              <Celula>{nomeDoModulo(recurso.modulo_chave)}</Celula>
              <Celula>{nomeDoProjetoJira(recurso.jira_project_id)}</Celula>
              <Celula>
                <Chip estado={recurso.ativo ? "ok" : "neutro"}>
                  {recurso.ativo ? "À venda" : "Desativado"}
                </Chip>
              </Celula>
              <Celula>
                <Botao
                  variante="fantasma"
                  tamanho="pequeno"
                  type="button"
                  data-testid={`editar-${recurso.chave}`}
                  onClick={() => abrirEdicao(recurso)}
                >
                  Editar
                </Botao>
              </Celula>
            </Linha>
          ))}
        </Corpo>
      </Tabela>
    </>
  );
}
